import { useEffect, useRef } from "react";
import { ADSENSE_CLIENT, ADS_ENABLED, loadAdSense, resolveAdSlot } from "@/lib/ads";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

type Format = "auto" | "fluid" | "horizontal" | "rectangle";

interface AdSlotProps {
  /** Named placement resolved from configured ad-unit ids. */
  placement?: string;
  /** Explicit AdSense ad unit id (data-ad-slot). */
  slot?: string;
  format?: Format;
  label?: string;
  className?: string;
}

/**
 * Renders a responsive AdSense unit when both the publisher id and a real
 * ad-unit id are configured. Renders nothing otherwise, so a page never shows
 * an empty "Advertisement" tray.
 */
export function AdSlot({ placement, slot, format = "auto", label = "Advertisement", className = "" }: AdSlotProps) {
  const ref = useRef<HTMLModElement>(null);
  const pushed = useRef(false);
  const resolvedSlot = resolveAdSlot(placement, slot);
  const enabled = ADS_ENABLED && resolvedSlot !== null;

  // The unit is only requested once it is close to the viewport, so an
  // above-the-fold page load never pays for the ad stack.
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    const fill = () => {
      if (pushed.current) return;
      pushed.current = true;
      loadAdSense();
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ad blocker or script blocked */
      }
    };
    if (typeof IntersectionObserver === "undefined") {
      fill();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          fill();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);

  if (!enabled) return null;

  return (
    <aside
      className={`my-10 overflow-hidden rounded-2xl border border-border ad-tray p-3 ${className}`}
      aria-label={label}
    >
      <p className="mb-2 text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </p>
      <ins
        ref={ref}
        className="adsbygoogle block"
        style={{ display: "block", minHeight: 90 }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={resolvedSlot}

        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
